/** @jsxImportSource @emotion/react */
import { Expression, Map as MBMap, FillLayer, FillExtrusionLayer } from 'mapbox-gl'
import { byAssetIdIn } from '../RiskMapView.utilities'
import { VectorTileset } from '@app/components/Molecules/LayersControl'
import { MapLayerStyle } from '@app/contexts/ModuleMapContexts/GenericMapContext/GenericMapContext.types'
import { AssetHazardInstances } from '../RiskMapView'

export interface VectorLayerProps {
  map: MBMap
  layerStyle: MapLayerStyle
  tileset: VectorTileset
  beforeId: string
  idAttribute?: string
  isVisible: boolean
  vulnerabilityData?: Map<string, number>
  vulnerabilityPalette?: string[]
  colorExpression?: Expression
  fillOpacityExpression?: Expression
  assetIds?: { [asset_id: number]: boolean }
  sortKeyExpression?: Expression
  hasFilters: boolean
  shouldUpdate?: boolean
  assetHazardInstances?: AssetHazardInstances
}

/**
 * Creates a polygon (optionally with extrusion) layer.
 * Styling is proivded either via colorExpression or with
 * vulnerabilityData & vulnerabilityPalette.
 */
export const addVectorLayer = ({
  map,
  layerStyle,
  tileset,
  beforeId,
  idAttribute,
  isVisible,
  vulnerabilityData,
  vulnerabilityPalette,
  colorExpression,
  fillOpacityExpression,
  assetIds,
  sortKeyExpression,
  hasFilters,
  shouldUpdate = false,
}: VectorLayerProps) => {
  const { id, sourceLayer } = tileset
  const fillLayerData: FillLayer = {
    id,
    type: 'fill',
    source: id,
    'source-layer': sourceLayer,
    layout: {
      visibility: isVisible ? 'visible' : 'none',
      'fill-sort-key': sortKeyExpression || 0,
    },
    paint: {
      'fill-opacity': fillOpacityExpression,
      ...layerStyle,
    },
  }

  // Special case: transparnet fill means "use the outline". Detect the transaprency
  // and apply the color rule to the fill or outline accordingly.
  const alphaIsZeroRegex = /rgba\(\d{1,3}, \d{1,3}, \d{1,3}, 0(\.0)?\)/

  const fillIsZero =
    layerStyle['fill-color'] && alphaIsZeroRegex.test(layerStyle['fill-color'] as unknown as string)
  const styleColorExpression = colorExpression || [
    'to-color',
    [
      'at',
      ['coalesce', ['feature-state', 'vulnerability_style'], 0],
      ['literal', vulnerabilityPalette],
    ],
  ]
  if (fillIsZero && fillLayerData.paint) {
    fillLayerData.paint['fill-outline-color'] = styleColorExpression
  }
  if (!fillIsZero && fillLayerData.paint) {
    fillLayerData.paint['fill-color'] = styleColorExpression
  }
  // ---

  const hasExtrusion = tileset.has_extrusion
  let extrusionLayer: FillExtrusionLayer | undefined

  if (hasExtrusion) {
    extrusionLayer = {
      id: `${id}-extrusion`,
      type: 'fill-extrusion',
      source: id,
      'source-layer': sourceLayer,
      layout: {
        visibility: isVisible ? 'visible' : 'none',
      },
      paint: {
        'fill-extrusion-color': [
          'to-color',
          [
            'at',
            ['coalesce', ['feature-state', 'vulnerability_style'], 0],
            ['literal', vulnerabilityPalette],
          ],
        ],
        'fill-extrusion-height': ['coalesce', ['get', 'Height (m)'], 0] as Expression,
        'fill-extrusion-opacity': 0.8,
        ...layerStyle,
      },
    }
  }

  if (shouldUpdate) {
    if (fillLayerData.paint) {
      for (const paintProp of Object.entries(fillLayerData.paint)) {
        map.setPaintProperty(fillLayerData.id, paintProp[0], paintProp[1])
      }
    }
    if (fillLayerData.layout) {
      for (const layoutProp of Object.entries(fillLayerData.layout)) {
        map.setLayoutProperty(fillLayerData.id, layoutProp[0], layoutProp[1])
      }
    }
    if (extrusionLayer?.paint) {
      for (const paintProp of Object.entries(extrusionLayer?.paint || {})) {
        map.setPaintProperty(`${id}-extrusion`, paintProp[0], paintProp[1])
      }
    }
    if (extrusionLayer?.layout) {
      for (const layoutProp of Object.entries(extrusionLayer?.layout || {})) {
        map.setLayoutProperty(`${id}-extrusion`, layoutProp[0], layoutProp[1])
      }
    }
    if (!hasFilters && !assetIds) {
      map.setFilter(id, null)
      if (extrusionLayer) map.setFilter(`${id}-extrusion`, null)
    }
  } else {
    map.addSource(id, { type: 'vector', url: `mapbox://${id}`, promoteId: idAttribute })
    if (extrusionLayer) map.addLayer(extrusionLayer, beforeId)
    map.addLayer(fillLayerData, beforeId)
  }
  if (hasFilters && assetIds) {
    const visibilityExpression: Expression = ['boolean', byAssetIdIn(assetIds)]
    map.setFilter(id, visibilityExpression)
    if (hasExtrusion) map.setFilter(`${id}-extrusion`, visibilityExpression)
  }

  // Replace feature state:
  map.removeFeatureState({
    source: id,
    sourceLayer: sourceLayer,
  })
  if (vulnerabilityData) {
    for (const [assetId, vulnerabilityStyle] of vulnerabilityData) {
      map.setFeatureState(
        {
          source: id,
          sourceLayer: sourceLayer,
          id: parseInt(assetId),
        },
        {
          vulnerability_style: vulnerabilityStyle,
        },
      )
    }
  }
}
